/**
 * The portaled country picker for {@link PhoneInput} (BL-1241, ADR 030 § 3b).
 *
 * Split out of `PhoneInput.tsx` so the core stays under the size threshold the
 * perf review lane enforces. The seam is a natural one: everything here concerns
 * the OPEN dropdown — its position, its search + keyboard navigation, and its
 * rows — and none of it runs while the field sits closed.
 *
 * WHY it portals to `document.body`: the buyer site's registration modal is an
 * `overflow-hidden` scroll container that clipped an absolutely-positioned list.
 * admin-web had the same latent bug in its own modals (Add Customer, Lienholder,
 * Profile) and simply had not had it reported. Positioning is structure rather
 * than appearance, so both apps get the fixed version.
 *
 * WHY the rows are `role="option"` divs and not `<button>`s: a button is not a
 * valid child of a `role="listbox"` per the ARIA authoring practices. admin-web
 * used buttons; the merged core takes the buyer site's correct shape.
 */
'use client';

import { useState, useRef, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import type { ReactNode, RefObject, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Check, Search } from 'lucide-react';
import { clsx } from 'clsx';
import { type CountryCode } from 'libphonenumber-js';
import { COUNTRY_OPTIONS, type CountryOption } from './phone-countries';
import type { PhoneInputDialect } from './phone-input-dialect';

export interface CountryDropdownProps {
    /** id applied to the portaled listbox so the trigger can aria-controls it. */
    id: string;
    country: CountryCode;
    dialect?: PhoneInputDialect;
    onSelect: (code: CountryCode) => void;
    onClose: () => void;
    buttonRef: RefObject<HTMLButtonElement | null>;
    /** The bordered row the portaled dropdown positions itself against. */
    anchorRef: RefObject<HTMLDivElement | null>;
}

/** Fixed-position coordinates for the portaled dropdown. */
interface DropdownPosition {
    left: number;
    /** Distance from the top of the viewport (used when opening downward). */
    top?: number;
    /** Distance from the bottom of the viewport (used when opening upward). */
    bottom?: number;
    width: number;
}

/** Max rendered height of the dropdown (search box + max-h-60 list + padding). */
const DROPDOWN_MAX_HEIGHT = 320;
/** Gap between the input and the dropdown. */
const DROPDOWN_GAP = 4;
/** Minimum rendered dropdown width. */
const DROPDOWN_MIN_WIDTH = 280;
/** Viewport edge inset so the dropdown never sits flush against the window. */
const DROPDOWN_VIEWPORT_INSET = 8;

/**
 * Compute fixed-position coordinates anchored to `anchor`, opening upward when
 * there isn't enough room below. Returns null if the anchor isn't mounted (or
 * called outside the browser, e.g. during SSR before hydration).
 * @param anchor - the element the dropdown aligns to
 * @returns viewport coordinates, or null when unmeasurable
 */
function computeDropdownPosition(anchor: HTMLElement | null): DropdownPosition | null {
    if (!anchor || typeof window === 'undefined') return null;
    const rect = anchor.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < DROPDOWN_MAX_HEIGHT && rect.top > spaceBelow;
    const width = Math.max(rect.width, DROPDOWN_MIN_WIDTH);
    // Clamp left so a min-width dropdown anchored to a narrow mobile input never
    // overflows the right viewport edge.
    const maxLeft = window.innerWidth - width - DROPDOWN_VIEWPORT_INSET;
    const left = Math.max(DROPDOWN_VIEWPORT_INSET, Math.min(rect.left, maxLeft));
    return {
        left,
        width,
        ...(openUp
            ? { bottom: window.innerHeight - rect.top + DROPDOWN_GAP }
            : { top: rect.bottom + DROPDOWN_GAP }),
    };
}

/**
 * Build the keydown handler for the country search box.
 * @returns a React keydown handler driving highlight, selection, and close
 */
function createDropdownKeyHandler(
    highlightedIndex: number,
    selectableIndices: number[],
    filteredOptions: CountryOption[],
    onSelect: (code: CountryCode) => void,
    onClose: () => void,
    buttonRef: RefObject<HTMLButtonElement | null>,
    setHighlightedIndex: (i: number) => void,
) {
    return (e: ReactKeyboardEvent) => {
        switch (e.key) {
            case 'ArrowDown': {
                e.preventDefault();
                const curPos = selectableIndices.indexOf(highlightedIndex);
                const nextPos = curPos < selectableIndices.length - 1 ? curPos + 1 : 0;
                setHighlightedIndex(selectableIndices[nextPos]);
                break;
            }
            case 'ArrowUp': {
                e.preventDefault();
                const curPos = selectableIndices.indexOf(highlightedIndex);
                const prevPos = curPos > 0 ? curPos - 1 : selectableIndices.length - 1;
                setHighlightedIndex(selectableIndices[prevPos]);
                break;
            }
            case 'Enter': {
                e.preventDefault();
                if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
                    onSelect(filteredOptions[highlightedIndex].value as CountryCode);
                }
                break;
            }
            case 'Escape': {
                e.preventDefault();
                e.stopPropagation();
                onClose();
                buttonRef.current?.focus();
                break;
            }
            case 'Tab': {
                onClose();
                break;
            }
        }
    };
}

/**
 * Filter the country list by a search string, dropping group headers whose
 * group ends up empty.
 * @param search - the current search text
 * @returns the visible options in render order
 */
function filterCountryOptions(search: string): CountryOption[] {
    if (!search) return COUNTRY_OPTIONS;
    const q = search.toLowerCase();
    const matched = COUNTRY_OPTIONS.filter((opt) => opt.isGroupHeader || opt.label.toLowerCase().includes(q));
    return matched.filter((opt, i) => {
        if (!opt.isGroupHeader) return true;
        // Keep a header only if a non-header entry follows it.
        const next = matched[i + 1];
        return next != null && !next.isGroupHeader;
    });
}

/**
 * Search + highlight state for the country list, plus the scroll-into-view and
 * reposition effects. Extracted so {@link CountryDropdown} stays under the
 * 100-line function cap.
 * @param opts - the selected country and the anchor to position against
 * @returns the dropdown's derived state, refs, and position
 */
function useCountryDropdownState(opts: {
    country: CountryCode;
    anchorRef: RefObject<HTMLDivElement | null>;
}) {
    const { country, anchorRef } = opts;
    const [search, setSearch] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    // Starts null; the useLayoutEffect below sets the real position synchronously
    // on mount — before the browser paints — so the first visible frame is
    // already anchored (no null-frame flash).
    const [position, setPosition] = useState<DropdownPosition | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Focus the search box the moment it attaches, NOT from a mount effect.
    // The list portals and renders null until `position` resolves, so on the
    // commit a mount effect fires the input does not exist yet and `focus()`
    // no-ops on null — the operator had to click the box before typing
    // (BUG-1261). A callback ref fires exactly when the node lands, however
    // many render passes the portal takes, and needs no coordination with the
    // position effect. The ref guard keeps it one-shot: a reposition re-render
    // must not re-focus, matching the highlight guard below.
    const didFocusSearchRef = useRef(false);
    const searchInputRef = useCallback((node: HTMLInputElement | null) => {
        if (!node || didFocusSearchRef.current) return;
        didFocusSearchRef.current = true;
        node.focus();
    }, []);

    useLayoutEffect(() => {
        setPosition(computeDropdownPosition(anchorRef.current));

        // Keep in sync while the surrounding modal/page scrolls or the viewport
        // resizes. `capture: true` catches scroll on a modal's inner scroll
        // container too. rAF-throttled so a fast scroll coalesces to one
        // getBoundingClientRect + setPosition per frame.
        let rafId = 0;
        const onMove = () => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => setPosition(computeDropdownPosition(anchorRef.current)));
        };
        window.addEventListener('scroll', onMove, true);
        window.addEventListener('resize', onMove);
        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener('scroll', onMove, true);
            window.removeEventListener('resize', onMove);
        };
        // anchorRef is a stable ref object, so this still runs exactly once.
    }, [anchorRef]);

    // Highlight the currently-selected country on open — strictly ONCE, which
    // the ref guard enforces rather than an empty dep array (packages/ui
    // registers no react-hooks plugin, so an exhaustive-deps suppression here
    // would be an "unknown rule" lint error).
    //
    // The guard is load-bearing, not cosmetic. Selecting a country closes the
    // dropdown, so USER-driven changes can't re-fire this — but `country` also
    // moves when the controlled `value` prop changes externally (a form reset,
    // a prefill landing late). If that happens while the list is open, a
    // dep-driven re-run would reset the operator's keyboard highlight
    // mid-navigation. Neither pre-merge implementation did that, and the merge
    // must not introduce it. (Focus is guarded separately, on the search box's
    // callback ref above — the same one-shot semantics, enforced where the node
    // actually mounts.)
    const didInitRef = useRef(false);
    useEffect(() => {
        if (didInitRef.current) return;
        didInitRef.current = true;
        const idx = COUNTRY_OPTIONS.findIndex((o) => o.value === country);
        setHighlightedIndex(idx >= 0 ? idx : 0);
    }, [country]);

    const filteredOptions = useMemo(() => filterCountryOptions(search), [search]);

    const selectableIndices = useMemo(() => {
        const indices: number[] = [];
        filteredOptions.forEach((opt, i) => { if (!opt.isGroupHeader) indices.push(i); });
        return indices;
    }, [filteredOptions]);

    useEffect(() => { if (search) setHighlightedIndex(selectableIndices[0] ?? -1); }, [search, selectableIndices]);

    useEffect(() => {
        if (highlightedIndex < 0 || !dropdownRef.current) return;
        dropdownRef.current.querySelector(`[data-option-index="${highlightedIndex}"]`)?.scrollIntoView({ block: 'nearest' });
    }, [highlightedIndex]);

    return {
        search, setSearch, highlightedIndex, setHighlightedIndex,
        position, dropdownRef, searchInputRef, filteredOptions, selectableIndices,
    };
}

/** Searchable, portaled dropdown list of countries with keyboard navigation. */
export function CountryDropdown({ id, country, dialect, onSelect, onClose, buttonRef, anchorRef }: CountryDropdownProps) {
    const {
        search, setSearch, highlightedIndex, setHighlightedIndex,
        position, dropdownRef, searchInputRef, filteredOptions, selectableIndices,
    } = useCountryDropdownState({ country, anchorRef });

    const handleKeyDown = useMemo(
        () => createDropdownKeyHandler(highlightedIndex, selectableIndices, filteredOptions, onSelect, onClose, buttonRef, setHighlightedIndex),
        [highlightedIndex, selectableIndices, filteredOptions, onSelect, onClose, buttonRef],
    );

    // typeof document guard keeps the render phase SSR-safe even though the
    // parent only mounts this on a client click.
    if (!position || typeof document === 'undefined') return null;

    return createPortal(
        <div
            data-phone-country-dropdown="true"
            style={{ position: 'fixed', left: position.left, top: position.top, bottom: position.bottom, width: position.width }}
            className={clsx('z-[60] border rounded-lg shadow-lg overflow-hidden', dialect?.dropdown)}
        >
            <div className={clsx('p-2 border-b', dialect?.searchWrap)}>
                <div className="relative">
                    <Search className={clsx('absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4', dialect?.searchIcon)} />
                    <input
                        ref={searchInputRef}
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Search countries..."
                        aria-label="Search countries"
                        className={clsx('w-full pl-8 pr-3 py-1.5 text-sm border rounded focus:outline-none', dialect?.searchInput)}
                    />
                </div>
            </div>

            {/* id lives on the listbox itself so the trigger's aria-controls points
                directly at the role="listbox" element (ARIA authoring practices). */}
            <div id={id} className="max-h-60 overflow-auto" ref={dropdownRef} role="listbox" aria-label="Select country">
                {filteredOptions.map((option, idx) =>
                    option.isGroupHeader ? (
                        <div key={option.value} role="presentation" className={dialect?.groupHeader}>
                            {option.label}
                        </div>
                    ) : (
                        <CountryOptionRow
                            key={option.value}
                            option={option}
                            index={idx}
                            dialect={dialect}
                            isSelected={option.value === country}
                            isHighlighted={highlightedIndex === idx}
                            onSelect={onSelect}
                            onHighlight={setHighlightedIndex}
                        />
                    ),
                )}
                {selectableIndices.length === 0 && (
                    <div className={dialect?.emptyState}>No countries found</div>
                )}
            </div>
        </div>,
        document.body,
    );
}

// ---------------------------------------------------------------------------

/**
 * One country row in the dropdown.
 *
 * A `<div role="option">` rather than a `<button>`: a button is not a valid
 * child of a `role="listbox"`. Keyboard interaction lives on the search box's
 * handler, which is why `tabIndex={-1}` is correct here.
 * @param props - the option, its index, dialect, and selection state
 * @returns the rendered option row
 */
function CountryOptionRow({
    option,
    index,
    dialect,
    isSelected,
    isHighlighted,
    onSelect,
    onHighlight,
}: {
    option: CountryOption;
    index: number;
    dialect?: PhoneInputDialect;
    isSelected: boolean;
    isHighlighted: boolean;
    onSelect: (code: CountryCode) => void;
    onHighlight: (index: number) => void;
}): ReactNode {
    return (
        <div
            data-option-index={index}
            role="option"
            aria-selected={isSelected}
            tabIndex={-1}
            onClick={() => onSelect(option.value as CountryCode)}
            onMouseEnter={() => onHighlight(index)}
            className={clsx(
                'w-full px-3 py-2 text-left text-sm transition-colors flex items-center justify-between cursor-pointer',
                dialect?.option,
                // Exclusive — a row is either the selection or it isn't.
                isSelected ? dialect?.optionSelected : dialect?.optionRest,
                // Additive — highlight layers over whichever of the two applied.
                isHighlighted && dialect?.optionHighlighted,
            )}
        >
            <span>{option.label}</span>
            {isSelected && <Check className={clsx('w-4 h-4', dialect?.optionCheck)} />}
        </div>
    );
}
